import axios from 'axios';
import {
  Platform,
  airflow,
  dict,
  payments,
  shop,
  accounts,
  lowPrices,
  ecommerce,
  products,
  loyalty,
  type PfmConfig,
  type AxiosStatic,
} from '@aviata/platform-client';
import { appLocale, seo, blog, hotOffers, specialOffers } from 'shared';
import {
  amplitudeLib,
  tokenStorage,
  freedomAuth,
  withAmplitudeDeviceId,
} from 'freedom-shared';

import { appConfig } from '@/shared/config';
import { sentryModel, useUserStore } from '@/shared/model';
import { getCfTurnstileChallenge } from '@/shared/lib/cf-turnstile';

import { apiClientLib } from './lib';

function createPfm() {
  const { origin } = window.location;
  const storage = tokenStorage.model.create('freedom-avia:consumer-token');

  const consumerToken = storage.getConsumerToken();
  const shouldTryRefreshToken = Boolean(consumerToken);

  const config: PfmConfig = {
    baseURL: appConfig.pfm.baseUrl,
    headers: {
      'x-site-url': origin, // This header is used by backend to build back links
      'accept-language': appLocale.lib.getAppLocale(),
    },
    consumer: {
      ...appConfig.pfm.consumer,
      token: consumerToken,
    },
    user: {
      required: true,
    },
    initStrategy: apiClientLib.getInitStrategy(shouldTryRefreshToken),
    timeout: 120000,
  };

  // not all modules are needed, you need to research which modules are needed and remove the modules that are not used
  const modules = {
    airflow: airflow({
      polling: {
        intervals: appConfig.pfm.pollingIntervals,
        timeoutInSec: 60,
      },
    }),
    payments,
    shop,
    dict,
    accounts,
    lowPrices,
    ecommerce,
    products,
    loyalty,
    seo: seo.api,
    blog: blog.api,
    hotOffers: hotOffers.api,
    specialOffers: specialOffers.api,
  };

  // Need to typecast to AxiosStatic from @aviata/platform-client because of the incompatibility of type
  const pfm = Platform(axios as AxiosStatic, config, modules);

  getCfTurnstileChallenge()?.withAxiosInterceptors(pfm.client);
  withAmplitudeDeviceId(pfm.client);

  const onPfmError = () => {
    amplitudeLib.invalidateUser();
    storage.removeConsumerToken();
    pfm.auth.token();
  };

  pfm.emitter
    .on('consumer-token-update', consumerToken =>
      storage.setConsumerToken(consumerToken),
    )
    .on('refresh-token-error', onPfmError);

  return pfm;
}

export const pfm = createPfm();

pfm.client.interceptors.request.use(config => {
  if (config.url?.startsWith('/doc-ai/')) {
    config.baseURL = appConfig.pfm.docAiBaseUrl || appConfig.pfm.baseUrl;
  }
  return config;
});

pfm.client.interceptors.request.use(
  (config: axios.AxiosResponse<unknown, unknown>) => {
    const userStore = useUserStore();
    return freedomAuth.lib
      .unauthorizedAccessInterceptor(config, userStore)
      .catch(() => {
        sentryModel.captureException(
          sentryModel.getReadableError(
            sentryModel.createCustomErrorText(
              sentryModel.errorPrefix.DEFAULT,
              'UNAUTHORIZED_ACCESS',
            ),
          ),
          {
            level: sentryModel.level.FATAL,
            extra: { request: config.endpointTag },
          },
        );
        return Promise.resolve(config);
      });
  },
);
